import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  OKTA_CLIENT_ID,
  OKTA_CLIENT_ID_INTERNAL,
  OKTA_ISSUER_URL,
  OKTA_ISSUER_URL_INTERNAL,
  PARTNER_APP_MIN_VERSION_FOR_AUTH,
} from '@constants/config';
import { compareSemver } from '@utils/compare';

import { OktaClient } from './Okta/client';
import { PartnerAppClient } from './PartnerApp/client';
import { EventTypes, emitter } from './event';
import { ClientType } from './types';
import { getBaseUrl } from './utils';

interface AuthProviderProps {
  children: ReactNode;
}

export const OKTA_CACHE_STORAGE = 'okta-cache-storage';
export const OKTA_SHARED_TRANSACTION_STORAGE =
  'okta-shared-transaction-storage';

type AuthClients = {
  [ClientType.OktaExternal]?: OktaClient;
  [ClientType.OktaInternal]?: OktaClient;
  [ClientType.PartnerApp]?: PartnerAppClient;
};

const isPartnerApp = () => {
  const match = window.navigator.userAgent.match(
    /uwPartnerApp\/(\d+\.\d+\.\d+)/
  );

  if (!match) {
    return false;
  }
  // Only this version and above supports sharing the auth token.
  return compareSemver(match[1], PARTNER_APP_MIN_VERSION_FOR_AUTH) >= 0;
};

const getAvailableClients = (): AuthClients => {
  const clients = {} as AuthClients;

  if (isPartnerApp()) {
    clients[ClientType.PartnerApp] = new PartnerAppClient({
      clientType: ClientType.PartnerApp,
    });
  } else {
    if (OKTA_CLIENT_ID && OKTA_ISSUER_URL) {
      clients[ClientType.OktaExternal] = new OktaClient({
        clientId: OKTA_CLIENT_ID,
        issuer: OKTA_ISSUER_URL,
        redirectUri: `${getBaseUrl()}/auth/okta/external/callback`,
        postLogoutRedirectUri: getBaseUrl(),
        storageKey: 'quote',
        clientType: ClientType.OktaExternal,
      });
    }

    if (OKTA_ISSUER_URL_INTERNAL && OKTA_CLIENT_ID_INTERNAL) {
      clients[ClientType.OktaInternal] = new OktaClient({
        clientId: OKTA_CLIENT_ID_INTERNAL,
        issuer: OKTA_ISSUER_URL_INTERNAL,
        redirectUri: `${getBaseUrl()}/auth/okta/internal/callback`,
        postLogoutRedirectUri: getBaseUrl(),
        storageKey: 'quote-sales',
        clientType: ClientType.OktaInternal,
      });
    }
  }

  return clients;
};

type AuthContextState = {
  logout: () => void;
  getToken: () => Promise<string | null>;
  activeClient: ClientType | null;
  clients: AuthClients;
  authInitialized: boolean;
  authError: string | null;
};

const AuthContext = createContext<AuthContextState>({
  logout: () => {},
  getToken: async () => null,
  activeClient: null,
  authInitialized: false,
  authError: null,
  clients: getAvailableClients(),
});

const AuthProvider = ({ children }: AuthProviderProps) => {
  const context = useContext(AuthContext);
  // Auth clients initialise outside React via an event emitter.
  // Use the functional state updater so each event receives
  // the latest readyClients value even if multiple events fire
  // before React re-renders.
  const [readyClients, setReadyClients] = useState<ClientType[]>([]);
  const [activeClient, setActiveClient] = useState<ClientType | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const authInitialized = Object.keys(context.clients).every((key) =>
    readyClients.includes(key as ClientType)
  );

  const handleReadyClient = useCallback((client: ClientType) => {
    setReadyClients((current) => {
      if (current.includes(client)) return current;
      return [...current, client];
    });
  }, []);

  // listeners and client initialisation are both in the one-time useEffect to
  // make sure we can start the listeners before the clients are initialised.
  // This is to ensure that they're listening to client start events. Login
  // routes may not have accurate state information otherwise.
  useEffect(() => {
    const handleActiveClient = (client: ClientType) => setActiveClient(client);
    const handleAuthError = (error: string) => setAuthError(error);

    emitter.on(EventTypes.ActiveClient, handleActiveClient);
    emitter.on(EventTypes.AuthError, handleAuthError);
    emitter.on(EventTypes.ClientInitialized, handleReadyClient);

    // we're initializing the clients manually here for now
    // the original intent was to only init a client when it was
    // in use, so we don't have redundant auth floating about doing nothing
    // but this may change
    Object.values(context.clients).forEach((client) => client.init());

    return () => {
      emitter.off(EventTypes.ActiveClient, handleActiveClient);
      emitter.off(EventTypes.AuthError, handleAuthError);
      emitter.off(EventTypes.ClientInitialized, handleReadyClient);
    };
  }, [context.clients, handleReadyClient]);

  const getToken = useCallback(async () => {
    if (activeClient) {
      return context.clients[activeClient]?.getOrRenewToken() ?? null;
    }

    return null;
  }, [context, activeClient]);

  const logout = useCallback(() => {
    if (activeClient) {
      context.clients[activeClient]?.logout();
    }
  }, [context, activeClient]);
  const value = useMemo(
    () => ({
      ...context,
      getToken,
      logout,
      activeClient,
      authError,
      authInitialized,
    }),
    [context, getToken, logout, activeClient, authError, authInitialized]
  );

  if (!authInitialized) {
    return null;
  }

  return <AuthContext value={value}>{children}</AuthContext>;
};

const useAuthContext = () => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuthContext };
